import {
  type AiResult,
  type ExportRecord,
  normalizeHex,
  normalizeLegendKeys,
  type Photo,
  type ProjectDoc,
  type ProjectRecord,
  type ProjectState,
  projectDocOf,
  pruneMeasuredFt,
} from '@better-lights/core';
import { formatDate } from '../lib/format.ts';
import { normalizeBlobUri, shaOfUri } from './blobStore.ts';

export const SCHEMA_VERSION = 1 as const;

/** The photo as stored: `uri` = 'sha256:<hex>' (BlobRef) plus optional blob metadata. */
export interface StoredPhoto extends Photo {
  sha256?: string;
  bytes?: number;
  mime?: string;
}

/**
 * A saved project: the shared ProjectRecord shape (contracts/schemas/project.schema.json,
 * schemaVersion 1). Blob URIs are API BlobRefs ('sha256:<hex>'). Unknown keys are preserved
 * on every round trip (forward compatibility with newer clients / sync metadata).
 */
export interface ProjectFile {
  schemaVersion: typeof SCHEMA_VERSION;
  id: string;
  name?: string;
  createdAt: number;
  updatedAt: number;
  photo: StoredPhoto;
  /** Cover thumbnail: BlobRef (`sha256:<hex>`) of a 512 px long-side JPEG (schema `thumb`; optional). */
  thumb?: string;
  doc: ProjectDoc & Record<string, unknown>;
  aiResults: AiResult[];
  exports: ExportRecord[];
  [key: string]: unknown;
}

/** Normalizes a doc: uppercase hex for run line colors, draw color and legend keys (fixed §B9). */
export function normalizeDoc<T extends ProjectDoc>(doc: T): T {
  return {
    ...doc,
    runs: doc.runs.map((r) =>
      r.lineColor === normalizeHex(r.lineColor) ? r : { ...r, lineColor: normalizeHex(r.lineColor) },
    ),
    drawLineColor: normalizeHex(doc.drawLineColor),
    legendLabels: normalizeLegendKeys(doc.legendLabels),
  };
}

/** The core reducer's view of a saved project (for `loadProject`). */
export function toProjectRecord(file: ProjectFile): ProjectRecord {
  return {
    id: file.id,
    name: file.name,
    createdAt: file.createdAt,
    updatedAt: file.updatedAt,
    photo: { uri: file.photo.uri, width: file.photo.width, height: file.photo.height },
    doc: normalizeDoc(projectDocOf(file.doc)),
    aiResults: file.aiResults,
    exports: file.exports,
  };
}

/**
 * `doc.measuredFt` to save (docs/spec/09 §1, openapi `doc.measuredFt`): positive feet for colors
 * that still have a legend row (uppercase keys); a color that had a value before and is now
 * cleared (or lost its row) is saved as `null`, which tells the server to delete it in the
 * key-by-key merge (the server drops nulls; pulled documents never contain them).
 */
export function savedMeasuredFt(state: ProjectState, prev: ProjectFile): Record<string, number | null> | undefined {
  const kept: Record<string, number | null> = { ...(pruneMeasuredFt(state.measuredFt, state.runs) ?? {}) };
  for (const [k, v] of Object.entries(kept)) if (typeof v !== 'number' || !(v > 0)) kept[k] = null;
  const before = (prev.doc as { measuredFt?: Record<string, number | null> }).measuredFt ?? {};
  for (const k of Object.keys(before)) {
    const key = normalizeHex(k);
    if (!(key in kept)) kept[key] = null;
  }
  return Object.keys(kept).length ? kept : undefined;
}

function withMeasured<T extends object>(doc: T, measuredFt?: Record<string, number | null>): T {
  const { measuredFt: _m, ...rest } = doc as T & { measuredFt?: unknown };
  return (measuredFt ? { ...rest, measuredFt } : rest) as T;
}

/** Writes the open project's state into its saved file, keeping unknown keys of `prev`. */
export function fileFromState(state: ProjectState, prev: ProjectFile, now: number): ProjectFile {
  return {
    ...prev,
    schemaVersion: SCHEMA_VERSION,
    updatedAt: now,
    doc: withMeasured({ ...prev.doc, ...projectDocOf(state) }, savedMeasuredFt(state, prev)),
    aiResults: state.aiResults,
    exports: state.exports,
  };
}

/** True when the saved parts of two states differ (autosave trigger). */
export function savedPartsChanged(a: ProjectState, b: ProjectState): boolean {
  return (
    a.projectId !== b.projectId ||
    a.photo !== b.photo ||
    a.runs !== b.runs ||
    a.time !== b.time ||
    a.colorId !== b.colorId ||
    a.style !== b.style ||
    a.frameWidthFt !== b.frameWidthFt ||
    a.drawLineColor !== b.drawLineColor ||
    a.legendLabels !== b.legendLabels ||
    a.measuredFt !== b.measuredFt ||
    a.view !== b.view ||
    a.aiResults !== b.aiResults ||
    a.exports !== b.exports
  );
}

/**
 * Reads any supported project JSON: current files, and legacy ProjectRecords without
 * schemaVersion (they gain schemaVersion 1). Throws on anything unusable.
 */
export function readProjectJson(raw: unknown): ProjectFile {
  if (!raw || typeof raw !== 'object') throw new Error('This is not a Better Lights project.');
  const r = raw as Record<string, unknown>;
  const version = r.schemaVersion ?? SCHEMA_VERSION;
  if (version !== SCHEMA_VERSION)
    throw new Error('This project was made by a newer version of Better Lights. Update the app to open it.');
  const photo = r.photo as StoredPhoto | undefined;
  const doc = r.doc as ProjectDoc | undefined;
  if (typeof r.id !== 'string' || !photo || typeof photo.uri !== 'string' || !doc || !Array.isArray(doc.runs)) {
    throw new Error('This is not a Better Lights project.');
  }
  return normalizeFileUris({
    ...(r as object),
    schemaVersion: SCHEMA_VERSION,
    id: r.id,
    createdAt: typeof r.createdAt === 'number' ? r.createdAt : Date.now(),
    updatedAt: typeof r.updatedAt === 'number' ? r.updatedAt : Date.now(),
    photo,
    doc: normalizeDoc({ ...doc, style: { ...doc.style } }) as ProjectFile['doc'],
    aiResults: Array.isArray(r.aiResults) ? (r.aiResults as AiResult[]) : [],
    exports: Array.isArray(r.exports) ? (r.exports as ExportRecord[]) : [],
  } as ProjectFile);
}

/** Earlier web builds stored `thumb` as `{uri, width, height}`; the schema says a BlobRef string. */
function thumbRef(thumb: unknown): string | undefined {
  if (typeof thumb === 'string') return normalizeBlobUri(thumb);
  if (thumb && typeof thumb === 'object' && typeof (thumb as { uri?: unknown }).uri === 'string') {
    return normalizeBlobUri((thumb as { uri: string }).uri);
  }
  return undefined;
}

/**
 * Read-time migrations of locally stored data: legacy `blobs/<hex>` URIs → BlobRefs (`sha256:<hex>`)
 * and the old object-form `thumb` → BlobRef string. Returns the same object if nothing changed.
 */
export function normalizeFileUris(f: ProjectFile): ProjectFile {
  const legacy = (u: string | undefined) => !!u && u.startsWith('blobs/');
  const thumb = thumbRef(f.thumb);
  if (
    thumb === f.thumb &&
    !legacy(f.photo.uri) &&
    !f.aiResults.some((a) => legacy(a.uri)) &&
    !f.exports.some((e) => legacy(e.uri))
  ) {
    return f;
  }
  const out: ProjectFile = {
    ...f,
    photo: { ...f.photo, uri: normalizeBlobUri(f.photo.uri) },
    aiResults: f.aiResults.map((a) => ({ ...a, uri: normalizeBlobUri(a.uri) })),
    exports: f.exports.map((e) => ({ ...e, uri: normalizeBlobUri(e.uri) })),
  };
  if (thumb) out.thumb = thumb;
  else delete out.thumb;
  return out;
}

/** Every blob hash a project references (photo, thumb, renders, exports). */
export function referencedBlobs(file: ProjectFile): string[] {
  const uris = [file.photo.uri, file.thumb, ...file.aiResults.map((a) => a.uri), ...file.exports.map((e) => e.uri)];
  return [...new Set(uris.map((u) => (u ? shaOfUri(u) : null)).filter((s): s is string => s !== null))];
}

/** Gallery title: the name, else the creation date. */
export function projectTitle(file: Pick<ProjectFile, 'name' | 'createdAt'>): string {
  return file.name || formatDate(file.createdAt);
}

/** Gallery cover: newest AI render, else the thumbnail, else the photo. */
export function coverUriOf(file: ProjectFile): string {
  return file.aiResults[0]?.uri ?? file.thumb ?? file.photo.uri;
}
